import React, { useState, useEffect, useRef } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import * as XLSX from 'xlsx';
import { Avatar, CompanyLogo } from '../utils/avatar';

interface AccessManagementProps {
  activeSubTab?: string;
  onSubTabChange?: (subTab: string) => void;
}

const AccessManagement: React.FC<AccessManagementProps> = ({ activeSubTab, onSubTabChange }) => {
  const { darkMode } = useTheme();
  const [activeAccessTab, setActiveAccessTab] = useState(activeSubTab || 'users');
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [scopes, setScopes] = useState<any[]>([]);
  const [groupScopes, setGroupScopes] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    job_title: '',
    group_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any | null>(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [showGroupDetailModal, setShowGroupDetailModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [groupUsers, setGroupUsers] = useState<any[]>([]);
  const [loadingGroupUsers, setLoadingGroupUsers] = useState(false);
  const [groupUsersPage, setGroupUsersPage] = useState(1);
  const [groupUsersPerPage] = useState(10);
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [editingScope, setEditingScope] = useState<any | null>(null);
  const [scopeFormData, setScopeFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true
  });
  const [updatingPermissions, setUpdatingPermissions] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    if (activeSubTab) {
      setActiveAccessTab(activeSubTab);
    }
  }, [activeSubTab]);

  // Load groups (needed for all tabs)
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groupsData = await apiService.getGroups();
        setGroups(groupsData);
      } catch (error: any) {
        console.error('Error loading groups:', error);
      }
    };
    loadGroups();
  }, []);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        if (activeAccessTab === 'users') {
          const [usersData, userGroupsData] = await Promise.all([
            apiService.getUsers(),
            apiService.getUserGroups()
          ]);
          setUsers(usersData);
          setUserGroups(userGroupsData);
        } else if (activeAccessTab === 'groups') {
          const groupsData = await apiService.getGroups();
          setGroups(groupsData);
        } else if (activeAccessTab === 'permissions') {
          try {
            const [scopesData, groupScopesData] = await Promise.all([
              apiService.getScopes(),
              apiService.getGroupScopes()
            ]);
            console.log('Scopes data loaded:', scopesData);
            console.log('Group scopes data loaded:', groupScopesData);
            setScopes(scopesData || []);
            setGroupScopes(groupScopesData || []);
          } catch (err: any) {
            console.error('Error loading permissions:', err);
            setError('Gagal memuat data permissions: ' + (err.message || 'Unknown error'));
            setScopes([]);
            setGroupScopes([]);
          }
        }
      } catch (error: any) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [activeAccessTab]);

  // Helper function to get user's group name
  const getUserGroup = (userId: number): string => {
    const userGroup = userGroups.find(ug => ug.user_id === userId);
    if (!userGroup) return 'No Group';
    const group = groups.find(g => g.id === userGroup.group_id);
    return group ? group.name : 'Unknown';
  };

  // Filter users based on search term and exclude super_admin
  const filteredUsers = users.filter(user => 
    user.role !== 'super_admin' &&
    (user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );


  // Get permission for a scope-group combination
  const getPermission = (scopeId: number, groupId: number) => {
    return groupScopes.find(
      (gs: any) => gs.scope_id === scopeId && gs.group_id === groupId
    );
  };

  // Handle Add Scope button
  const handleAddScope = () => {
    setEditingScope(null);
    setScopeFormData({
      name: '',
      code: '',
      description: '',
      is_active: true
    });
    setError(null);
    setSuccess(null);
    setShowScopeModal(true);
  };

  // Handle Edit Scope button
  const handleEditScope = (scope: any) => {
    setEditingScope(scope);
    setScopeFormData({
      name: scope.name || '',
      code: scope.code || '',
      description: scope.description || '',
      is_active: scope.is_active !== undefined ? scope.is_active : true
    });
    setError(null);
    setSuccess(null);
    setShowScopeModal(true);
  };

  // Handle Scope input change
  const handleScopeInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setScopeFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // Handle Save Scope
  const handleSaveScope = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingScope) {
        // Update existing scope
        await apiService.updateScope(editingScope.id, scopeFormData);
        setSuccess('Scope updated successfully');
      } else {
        // Create new scope
        await apiService.createScope(scopeFormData);
        setSuccess('Scope created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowScopeModal(false);
      }, 1500);

      // Reload scopes and group scopes
      const [scopesData, groupScopesData] = await Promise.all([
        apiService.getScopes(),
        apiService.getGroupScopes()
      ]);
      setScopes(scopesData || []);
      setGroupScopes(groupScopesData || []);
    } catch (error: any) {
      setError(error.message || 'Failed to save scope');
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete Scope
  const handleDeleteScope = async (scopeId: number) => {
    if (!window.confirm('Are you sure you want to delete this scope? This will also remove all permissions associated with it.')) {
      return;
    }

    try {
      await apiService.deleteScope(scopeId);
      setSuccess('Scope deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      // Reload scopes and group scopes
      const [scopesData, groupScopesData] = await Promise.all([
        apiService.getScopes(),
        apiService.getGroupScopes()
      ]);
      setScopes(scopesData || []);
      setGroupScopes(groupScopesData || []);
    } catch (error: any) {
      setError(error.message || 'Failed to delete scope');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle permission checkbox change (Yes/No)
  const handlePermissionChange = async (
    scopeId: number,
    groupId: number,
    checked: boolean
  ) => {
    const key = `${scopeId}-${groupId}`;
    setUpdatingPermissions(prev => ({ ...prev, [key]: true }));

    try {
      const existingPermission = getPermission(scopeId, groupId);
      
      if (existingPermission) {
        // Update existing permission
        await apiService.updateGroupScopePermissions(existingPermission.id, {
          can_access: checked
        });
      } else {
        // Create new permission
        await apiService.assignScopeToGroup(groupId, scopeId, {
          can_access: checked
        });
      }

      // Reload group scopes
      const groupScopesData = await apiService.getGroupScopes();
      setGroupScopes(groupScopesData || []);
    } catch (error: any) {
      setError(error.message || 'Failed to update permission');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUpdatingPermissions(prev => ({ ...prev, [key]: false }));
    }
  };

  // Reload users data
  const reloadUsers = async () => {
    try {
      const [usersData, userGroupsData] = await Promise.all([
        apiService.getUsers(),
        apiService.getUserGroups()
      ]);
      setUsers(usersData);
      setUserGroups(userGroupsData);
    } catch (error: any) {
      console.error('Error reloading users:', error);
    }
  };

  // Handle Add User button
  const handleAddUser = () => {
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      job_title: '',
      group_id: ''
    });
    setError(null);
    setSuccess(null);
    setShowUserModal(true);
  };

  // Handle Edit User button
  const handleEditUser = (user: any) => {
    const userGroup = userGroups.find(ug => ug.user_id === user.id);
    setEditingUser(user);
    setUserFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      confirmPassword: '',
      job_title: user.job_title || '',
      group_id: userGroup ? userGroup.group_id.toString() : ''
    });
    setError(null);
    setSuccess(null);
    setShowUserModal(true);
  };

  // Handle Delete User
  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      await reloadUsers();
    } catch (error: any) {
      setError(error.message || 'Failed to delete user');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle Save User (Add or Edit)
  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate password confirmation for new users
      if (!editingUser) {
        if (!userFormData.password) {
          setError('Password is required');
          setSaving(false);
          return;
        }
        if (userFormData.password !== userFormData.confirmPassword) {
          setError('Password and confirmation password do not match');
          setSaving(false);
          return;
        }
      }

      const userData: any = {
        name: userFormData.name,
        email: userFormData.email,
        job_title: userFormData.job_title || null,
        role: 'user', // Always set to 'user'
        is_active: true // Always active
      };

      if (editingUser) {
        // Update existing user
        if (userFormData.password) {
          if (userFormData.password !== userFormData.confirmPassword) {
            setError('Password and confirmation password do not match');
            setSaving(false);
            return;
          }
          userData.password = userFormData.password;
        }
        await apiService.updateUser(editingUser.id, userData);
        
        // Update group assignment if changed
        const currentUserGroup = userGroups.find(ug => ug.user_id === editingUser.id);
        if (userFormData.group_id) {
          if (currentUserGroup) {
            // Remove old group assignment if different
            if (currentUserGroup.group_id.toString() !== userFormData.group_id) {
              await apiService.removeUserFromGroup(currentUserGroup.id);
              await apiService.assignUserToGroup(editingUser.id, parseInt(userFormData.group_id));
            }
          } else {
            // Add new group assignment
            await apiService.assignUserToGroup(editingUser.id, parseInt(userFormData.group_id));
          }
        } else if (currentUserGroup) {
          // Remove group assignment if group_id is empty
          await apiService.removeUserFromGroup(currentUserGroup.id);
        }
        setSuccess('User updated successfully');
      } else {
        // Create new user
        userData.password = userFormData.password;
        const response = await apiService.register(userData);
        
        // Assign to group if selected
        if (userFormData.group_id && response.user_id) {
          await apiService.assignUserToGroup(response.user_id, parseInt(userFormData.group_id));
        }
        setSuccess('User created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowUserModal(false);
      }, 1500);
      await reloadUsers();
    } catch (error: any) {
      setError(error.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // Reload groups data
  const reloadGroups = async () => {
    try {
      const groupsData = await apiService.getGroups();
      setGroups(groupsData);
    } catch (error: any) {
      console.error('Error reloading groups:', error);
    }
  };

  // Handle Add Group button
  const handleAddGroup = () => {
    setEditingGroup(null);
    setGroupFormData({
      name: '',
      description: '',
      is_active: true
    });
    setError(null);
    setSuccess(null);
    setShowGroupModal(true);
  };

  // Handle Edit Group button
  const handleEditGroup = (group: any) => {
    setEditingGroup(group);
    setGroupFormData({
      name: group.name || '',
      description: group.description || '',
      is_active: group.is_active !== undefined ? group.is_active : true
    });
    setError(null);
    setSuccess(null);
    setShowGroupModal(true);
  };

  // Handle View Group Detail
  const handleViewGroupDetail = async (group: any) => {
    setSelectedGroup(group);
    setShowGroupDetailModal(true);
    setGroupUsersPage(1); // Reset to first page
    setLoadingGroupUsers(true);
    
    try {
      // Fetch users in this group
      const userGroupsData = await apiService.getUserGroups(undefined, group.id);
      
      // Get user IDs from user_groups
      const userIds = userGroupsData.map((ug: any) => ug.user_id);
      
      // Fetch all users and filter by IDs
      const allUsers = await apiService.getUsers();
      const groupUsersList = allUsers.filter((user: any) => userIds.includes(user.id));
      
      setGroupUsers(groupUsersList);
    } catch (error: any) {
      console.error('Error loading group users:', error);
      setError('Failed to load group users');
    } finally {
      setLoadingGroupUsers(false);
    }
  };

  // Handle Delete Group
  const handleDeleteGroup = async (groupId: number) => {
    if (!window.confirm('Are you sure you want to delete this group? All user assignments and permissions will be removed.')) {
      return;
    }

    try {
      await apiService.deleteGroup(groupId);
      setSuccess('Group deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      await reloadGroups();
    } catch (error: any) {
      setError(error.message || 'Failed to delete group');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle Save Group (Add or Edit)
  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingGroup) {
        // Update existing group
        await apiService.updateGroup(editingGroup.id, groupFormData);
        setSuccess('Group updated successfully');
      } else {
        // Create new group
        await apiService.createGroup(groupFormData);
        setSuccess('Group created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowGroupModal(false);
      }, 1500);
      await reloadGroups();
    } catch (error: any) {
      setError(error.message || 'Failed to save group');
    } finally {
      setSaving(false);
    }
  };

  // Handle group input change
  const handleGroupInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleTabChange = (tab: string) => {
    setActiveAccessTab(tab);
    if (onSubTabChange) {
      onSubTabChange(tab);
    }
  };

  const accessTabs = [
    { id: 'users', label: 'Users' },
    { id: 'groups', label: 'Groups' },
    { id: 'permissions', label: 'Permissions' }
  ];

  return (
    <section className="py-8" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', paddingLeft: '2rem', paddingRight: '2rem', boxSizing: 'border-box' }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Access Management</h2>
      </div>

      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border transition-colors duration-300`} style={{ width: '100%', maxWidth: '100%' }}>
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex space-x-8 px-8 pt-4">
            {accessTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`pb-4 text-sm font-semibold transition-colors ${
                  activeAccessTab === tab.id
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-8" style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
          {activeAccessTab === 'users' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>User Management</h3>
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <i className={`fa-solid fa-magnifying-glass absolute left-3 top-3 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:border-violet-500 ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <button 
                    onClick={handleAddUser}
                    className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                  >
                    <i className="fa-solid fa-user-plus mr-2"></i>
                    Add User
                  </button>
                </div>
              </div>

              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden transition-colors duration-300`}>
                <table className="w-full">
                  <thead className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider w-12 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <input type="checkbox" className={`rounded ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>User</th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Group</th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Role</th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Last Active</th>
                      <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                          Loading users...
                        </td>
                      </tr>
                    ) : filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No users found
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => {
                        const userGroupName = getUserGroup(user.id);
                        const lastActive = user.last_login 
                          ? new Date(user.last_login).toLocaleString('id-ID', { 
                              day: 'numeric', 
                              month: 'short', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })
                          : 'Never';
                        return (
                          <tr key={user.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                            <td className="px-6 py-4">
                              <input type="checkbox" className={`rounded ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <Avatar 
                                  src={user.avatar_url} 
                                  name={user.name}
                                  id={user.id}
                                  className="w-8 h-8 rounded-full"
                                  alt={user.name}
                                />
                                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.email}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700'
                              }`}>
                                {userGroupName}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm capitalize ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.role?.replace('_', ' ')}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                user.is_active 
                                  ? darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {user.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{lastActive}</span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <button 
                                  onClick={() => handleEditUser(user)}
                                  className={`p-2 text-violet-600 rounded transition-colors ${
                                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-violet-50'
                                  }`}
                                  title="Edit"
                                >
                                  <i className="fa-solid fa-edit text-sm"></i>
                                </button>
                                <button 
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded" 
                                  title="Delete"
                                >
                                  <i className="fa-solid fa-trash text-sm"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* User Modal */}
              {showUserModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300`}>
                    <div className={`sticky top-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4 flex items-center justify-between`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingUser ? 'Edit User' : 'Add New User'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowUserModal(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveUser} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${
                            darkMode ? 'text-red-300' : 'text-red-700'
                          }`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${
                            darkMode ? 'text-green-300' : 'text-green-700'
                          }`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={userFormData.name}
                            onChange={handleInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={userFormData.email}
                            onChange={handleInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Password {!editingUser && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="password"
                            name="password"
                            value={userFormData.password}
                            onChange={handleInputChange}
                            required={!editingUser}
                            placeholder={editingUser ? "Leave blank to keep current password" : ""}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Confirm Password {!editingUser && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={userFormData.confirmPassword}
                            onChange={handleInputChange}
                            required={!editingUser}
                            placeholder={editingUser ? "Leave blank to keep current password" : ""}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Job Title
                          </label>
                          <input
                            type="text"
                            name="job_title"
                            value={userFormData.job_title}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Group
                          </label>
                          <select
                            name="group_id"
                            value={userFormData.group_id}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white'
                                : 'border-gray-300'
                            }`}
                          >
                            <option value="">No Group</option>
                            {groups.map(group => (
                              <option key={group.id} value={group.id}>
                                {group.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserModal(false);
                            setError(null);
                            setSuccess(null);
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingUser ? 'Update User' : 'Create User'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

          {activeAccessTab === 'groups' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Group Management</h3>
                <button 
                  onClick={handleAddGroup}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                >
                  <i className="fa-solid fa-user-group mr-2"></i>
                  Create Group
                </button>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {loading ? (
                  <div className={`col-span-3 text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Loading groups...
                  </div>
                ) : groups.length === 0 ? (
                  <div className={`col-span-3 text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No groups found
                  </div>
                ) : (
                  groups.map((group, idx) => {
                    const iconClasses = darkMode ? [
                      'bg-violet-900/50 text-violet-300',
                      'bg-green-900/50 text-green-300',
                      'bg-purple-900/50 text-purple-300',
                      'bg-gray-700 text-gray-300'
                    ] : [
                      'bg-violet-100 text-violet-600',
                      'bg-green-100 text-green-600',
                      'bg-purple-100 text-purple-600',
                      'bg-gray-100 text-gray-600'
                    ];
                    const iconClass = iconClasses[idx % iconClasses.length];
                    return (
                    <div 
                      key={group.id} 
                      className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-6 cursor-pointer hover:shadow-md transition-all duration-300`}
                      onClick={() => handleViewGroupDetail(group)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 ${iconClass} rounded-lg flex items-center justify-center`}>
                          <i className="fa-solid fa-user-group text-xl"></i>
                        </div>
                        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          <button 
                            onClick={() => handleEditGroup(group)}
                            className={`p-2 rounded transition-colors ${
                              darkMode
                                ? 'text-gray-400 hover:bg-gray-700'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <i className="fa-solid fa-edit text-sm"></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteGroup(group.id)}
                            className={`p-2 text-red-600 rounded transition-colors ${
                              darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                            }`}
                          >
                            <i className="fa-solid fa-trash text-sm"></i>
                          </button>
                        </div>
                      </div>
                      <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{group.name}</h3>
                      {group.description && (
                        <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{group.description}</p>
                      )}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Members</span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{group.member_count || 0}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Permissions</span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{group.permission_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                  })
                )}
              </div>

              {/* Group Modal */}
              {showGroupModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-lg w-full mx-4 transition-colors duration-300`}>
                    <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-6 py-4 flex items-center justify-between`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingGroup ? 'Edit Group' : 'Create New Group'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowGroupModal(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveGroup} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${
                            darkMode ? 'text-red-300' : 'text-red-700'
                          }`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${
                            darkMode ? 'text-green-300' : 'text-green-700'
                          }`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Group Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={groupFormData.name}
                            onChange={handleGroupInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="e.g., Legal Team"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={groupFormData.description}
                            onChange={handleGroupInputChange}
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Group description..."
                          />
                        </div>
                        <div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="is_active"
                              checked={groupFormData.is_active}
                              onChange={handleGroupInputChange}
                              className={`h-4 w-4 text-violet-600 focus:ring-violet-500 rounded ${
                                darkMode ? 'border-gray-600' : 'border-gray-300'
                              }`}
                            />
                            <label className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Active
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowGroupModal(false);
                            setError(null);
                            setSuccess(null);
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingGroup ? 'Update Group' : 'Create Group'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Group Detail Modal */}
              {showGroupDetailModal && selectedGroup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300`}>
                    <div className={`sticky top-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4 z-10`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedGroup.name}</h2>
                          {selectedGroup.description && (
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{selectedGroup.description}</p>
                          )}
                          <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total Members: <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{groupUsers.length}</span>
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setShowGroupDetailModal(false);
                            setSelectedGroup(null);
                            setGroupUsers([]);
                            setGroupUsersPage(1);
                          }}
                          className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <i className="fa-solid fa-times text-xl"></i>
                        </button>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="mb-4">
                        <h3 className={`text-base font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Group Members</h3>
                        
                        {loadingGroupUsers ? (
                          <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                            Loading users...
                          </div>
                        ) : groupUsers.length === 0 ? (
                          <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            No members in this group
                          </div>
                        ) : (
                          <>
                            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden transition-colors duration-300`}>
                              <table className="w-full">
                                <thead className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                                  <tr>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>User</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Job Title</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Role</th>
                                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                                  </tr>
                                </thead>
                                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                  {groupUsers
                                    .slice((groupUsersPage - 1) * groupUsersPerPage, groupUsersPage * groupUsersPerPage)
                                    .map((user: any) => (
                                    <tr key={user.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-3">
                                          <Avatar 
                                            src={user.avatar_url} 
                                            name={user.name}
                                            id={user.id}
                                            className="w-8 h-8 rounded-full"
                                            alt={user.name}
                                          />
                                          <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.email}</span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.job_title || '-'}</span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-medium rounded capitalize ${
                                          darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700'
                                        }`}>
                                          {user.role.replace('_', ' ')}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                                          user.is_active 
                                            ? darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                            : darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                        }`}>
                                          {user.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            
                            {/* Pagination */}
                            {groupUsers.length > groupUsersPerPage && (
                              <div className={`mt-4 px-4 py-3 border-t flex items-center justify-between rounded-b-lg ${
                                darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                              }`}>
                                <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                  Showing {(groupUsersPage - 1) * groupUsersPerPage + 1} - {Math.min(groupUsersPage * groupUsersPerPage, groupUsers.length)} of {groupUsers.length} members
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => setGroupUsersPage(Math.max(1, groupUsersPage - 1))}
                                    disabled={groupUsersPage === 1}
                                    className={`px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                      darkMode
                                        ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 hover:bg-white'
                                    }`}
                                  >
                                    <i className="fa-solid fa-chevron-left mr-1"></i>
                                    Previous
                                  </button>
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Page {groupUsersPage} of {Math.ceil(groupUsers.length / groupUsersPerPage)}
                                  </span>
                                  <button
                                    onClick={() => setGroupUsersPage(groupUsersPage + 1)}
                                    disabled={groupUsersPage >= Math.ceil(groupUsers.length / groupUsersPerPage)}
                                    className={`px-3 py-1 text-sm border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                      darkMode
                                        ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                                        : 'border-gray-300 hover:bg-white'
                                    }`}
                                  >
                                    Next
                                    <i className="fa-solid fa-chevron-right ml-1"></i>
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {activeAccessTab === 'permissions' && (
            <>
              {error && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                  darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                </div>
              )}
              {success && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                  darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{success}</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Permission Management</h3>
                <button 
                  onClick={handleAddScope}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Scope
                </button>
              </div>

              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden transition-colors duration-300`} style={{ width: '100%', maxWidth: '100%' }}>
                <div className="overflow-x-auto" style={{ width: '100%', maxWidth: '100%' }}>
                  <table style={{ width: 'max-content' }}>
                    <colgroup>
                      <col style={{ width: '250px', minWidth: '250px' }} />
                      <col style={{ width: '200px', minWidth: '200px' }} />
                      <col style={{ width: '300px', minWidth: '300px' }} />
                      {groups.map((_, index) => (
                        <col key={index} style={{ width: '150px', minWidth: '150px' }} />
                      ))}
                      <col style={{ width: '120px', minWidth: '120px' }} />
                    </colgroup>
                    <thead className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider sticky left-0 z-20 border-r ${darkMode ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`} style={{ width: '250px', minWidth: '250px' }}>
                          Scope Name
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ width: '200px', minWidth: '200px' }}>
                          Code
                        </th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ width: '300px', minWidth: '300px' }}>
                          Description
                        </th>
                        {groups.map((group) => (
                          <th key={group.id} className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ width: '150px', minWidth: '150px' }}>
                            <span className="whitespace-nowrap">{group.name}</span>
                          </th>
                        ))}
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} style={{ width: '120px', minWidth: '120px' }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {loading ? (
                        <tr>
                          <td colSpan={groups.length + 4} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                            Loading scopes...
                          </td>
                        </tr>
                      ) : scopes.length === 0 ? (
                        <tr>
                          <td colSpan={groups.length + 4} className={`px-6 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            No scopes found. Click "Add Scope" to create one.
                          </td>
                        </tr>
                      ) : (
                        scopes.map((scope) => {
                          return (
                            <tr key={scope.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                              <td className={`px-6 py-4 sticky left-0 z-10 border-r ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ width: '250px', minWidth: '250px' }}>
                                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{scope.name}</span>
                              </td>
                              <td className="px-6 py-4" style={{ width: '200px', minWidth: '200px' }}>
                                <code className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                                  darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                                }`}>{scope.code}</code>
                              </td>
                              <td className="px-6 py-4" style={{ width: '300px', minWidth: '300px' }}>
                                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{scope.description || '-'}</span>
                              </td>
                              {groups.map((group) => {
                                const permission = getPermission(scope.id, group.id);
                                const keyBase = `${scope.id}-${group.id}`;
                                return (
                                  <td key={group.id} className="px-4 py-4 text-center" style={{ width: '150px', minWidth: '150px' }}>
                                    <label className="flex items-center justify-center cursor-pointer group" title={permission?.can_access ? 'Has Access' : 'No Access'}>
                                      <input
                                        type="checkbox"
                                        checked={permission?.can_access || false}
                                        onChange={(e) => handlePermissionChange(scope.id, group.id, e.target.checked)}
                                        disabled={updatingPermissions[keyBase]}
                                        className={`w-5 h-5 text-violet-600 rounded focus:ring-violet-500 cursor-pointer disabled:cursor-not-allowed ${
                                          darkMode ? 'border-gray-600' : 'border-gray-300'
                                        }`}
                                      />
                                      {updatingPermissions[keyBase] && (
                                        <i className={`fa-solid fa-spinner fa-spin text-xs ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                                      )}
                                    </label>
                                  </td>
                                );
                              })}
                              <td className="px-6 py-4" style={{ width: '120px', minWidth: '120px' }}>
                                <div className="flex items-center space-x-2">
                                  <button 
                                    onClick={() => handleEditScope(scope)}
                                    className={`p-2 text-violet-600 rounded transition-colors ${
                                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-violet-50'
                                    }`}
                                    title="Edit Scope"
                                  >
                                    <i className="fa-solid fa-edit text-sm"></i>
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteScope(scope.id)}
                                    className={`p-2 text-red-600 rounded transition-colors ${
                                      darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                                    }`}
                                    title="Delete Scope"
                                  >
                                    <i className="fa-solid fa-trash text-sm"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Scope Modal */}
              {showScopeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300`}>
                    <div className={`sticky top-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4 flex items-center justify-between`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingScope ? 'Edit Scope' : 'Add New Scope'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowScopeModal(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveScope} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${
                            darkMode ? 'text-red-300' : 'text-red-700'
                          }`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${
                            darkMode ? 'text-green-300' : 'text-green-700'
                          }`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Scope Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={scopeFormData.name}
                            onChange={handleScopeInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="e.g., Dashboard View, Templates Create"
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="code"
                            value={scopeFormData.code}
                            onChange={handleScopeInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm font-mono ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="e.g., dashboard.view, templates.create"
                          />
                          <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Kode unik untuk scope (harus unique, gunakan format lowercase dengan dot atau underscore)
                          </p>
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={scopeFormData.description}
                            onChange={handleScopeInputChange}
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Deskripsi scope..."
                          />
                        </div>

                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              name="is_active"
                              checked={scopeFormData.is_active}
                              onChange={(e) => setScopeFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                              className={`mr-2 rounded text-violet-600 focus:ring-violet-500 ${
                                darkMode ? 'border-gray-600' : 'border-gray-300'
                              }`}
                            />
                            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Active</span>
                          </label>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowScopeModal(false);
                            setError(null);
                            setSuccess(null);
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingScope ? 'Update Scope' : 'Create Scope'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

interface ContentManagementProps {
  activeSubTab?: string;
  onSubTabChange?: (subTab: string) => void;
}

const ContentManagement: React.FC<ContentManagementProps> = ({ activeSubTab, onSubTabChange }) => {
  const { darkMode } = useTheme();
  const [activeContentTab, setActiveContentTab] = useState(activeSubTab || 'company');
  const [companies, setCompanies] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    code: '',
    description: '',
    logo_url: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [logoUploadMode, setLogoUploadMode] = useState<'url' | 'upload'>('url');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    icon_class: 'fa-folder text-violet-600'
  });
  const [showAgreementTypeModal, setShowAgreementTypeModal] = useState(false);
  const [editingAgreementType, setEditingAgreementType] = useState<any | null>(null);
  const [agreementTypeFormData, setAgreementTypeFormData] = useState({
    name: '',
    description: '',
    icon_class: 'fa-file-signature text-purple-600'
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<any | null>(null);
  const [statusFormData, setStatusFormData] = useState({
    name: '',
    description: '',
    color_class: 'bg-gray-100 text-gray-700',
    sla_days: '' as string,
    sort_order: '' as string
  });

  useEffect(() => {
    if (activeSubTab) {
      setActiveContentTab(activeSubTab);
    }
  }, [activeSubTab]);

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (activeContentTab === 'company') {
          const data = await apiService.getCompanies();
          setCompanies(data);
        } else if (activeContentTab === 'category') {
          const data = await apiService.getCategories();
          setCategories(data);
        } else if (activeContentTab === 'agreement-types') {
          const data = await apiService.getAgreementTypes();
          setAgreementTypes(data);
        } else if (activeContentTab === 'status') {
          const data = await apiService.getStatuses();
          setStatuses(data);
        }
      } catch (error: any) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [activeContentTab]);

  const handleTabChange = (tab: string) => {
    setActiveContentTab(tab);
    if (onSubTabChange) {
      onSubTabChange(tab);
    }
  };

  // Handle Add Company button
  const handleAddCompany = () => {
    setEditingCompany(null);
    setCompanyFormData({
      name: '',
      code: '',
      description: '',
      logo_url: '',
      is_active: true
    });
    setLogoUploadMode('url');
    setLogoPreview(null);
    setError(null);
    setSuccess(null);
    setShowCompanyModal(true);
  };

  // Handle Edit Company button
  const handleEditCompany = (company: any) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name || '',
      code: company.code || '',
      description: company.description || '',
      logo_url: company.logo_url || '',
      is_active: company.is_active !== undefined ? company.is_active : true
    });
    setLogoUploadMode(company.logo_url && company.logo_url.startsWith('data:') ? 'upload' : 'url');
    setLogoPreview(company.logo_url || null);
    setError(null);
    setSuccess(null);
    setShowCompanyModal(true);
  };

  // Handle Company Input Change
  const handleCompanyInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setCompanyFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Update preview if logo_url changes
    if (name === 'logo_url' && logoUploadMode === 'url') {
      setLogoPreview(value || null);
    }
  };

  // Handle Logo File Upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setCompanyFormData(prev => ({
          ...prev,
          logo_url: base64String
        }));
        setLogoPreview(base64String);
        setError(null);
      };
      reader.onerror = () => {
        setError('Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Save Company
  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Use logo_url from form data (either URL or base64 from upload)
      const logoUrl = companyFormData.logo_url || null;

      const companyData: any = {
        name: companyFormData.name,
        code: companyFormData.code,
        description: companyFormData.description || null,
        logo_url: logoUrl || null,
        is_active: companyFormData.is_active
      };

      if (editingCompany) {
        await apiService.updateCompany(editingCompany.id, companyData);
        setSuccess('Company updated successfully');
      } else {
        await apiService.createCompany(companyData);
        setSuccess('Company created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowCompanyModal(false);
      }, 1500);

      // Reload companies
      const data = await apiService.getCompanies();
      setCompanies(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  // Handle Add Category button
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      description: '',
      icon_class: 'fa-folder text-violet-600'
    });
    setError(null);
    setSuccess(null);
    setShowCategoryModal(true);
  };

  // Handle Edit Category button
  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      icon_class: category.icon_class || 'fa-folder text-violet-600'
    });
    setError(null);
    setSuccess(null);
    setShowCategoryModal(true);
  };

  // Handle Category Input Change
  const handleCategoryInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCategoryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Save Category
  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const categoryData: any = {
        name: categoryFormData.name,
        description: categoryFormData.description || null,
        icon_class: categoryFormData.icon_class || null
      };

      if (editingCategory) {
        await apiService.updateCategory(editingCategory.id, categoryData);
        setSuccess('Category updated successfully');
      } else {
        await apiService.createCategory(categoryData);
        setSuccess('Category created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowCategoryModal(false);
      }, 1500);

      // Reload categories
      const data = await apiService.getCategories();
      setCategories(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  // Handle Add Agreement Type button
  const handleAddAgreementType = () => {
    setEditingAgreementType(null);
    setAgreementTypeFormData({
      name: '',
      description: '',
      icon_class: 'fa-file-signature text-purple-600'
    });
    setError(null);
    setSuccess(null);
    setShowAgreementTypeModal(true);
  };

  // Handle Edit Agreement Type button
  const handleEditAgreementType = (type: any) => {
    setEditingAgreementType(type);
    setAgreementTypeFormData({
      name: type.name || '',
      description: type.description || '',
      icon_class: type.icon_class || 'fa-file-signature text-purple-600'
    });
    setError(null);
    setSuccess(null);
    setShowAgreementTypeModal(true);
  };

  // Handle Agreement Type Input Change
  const handleAgreementTypeInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAgreementTypeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Save Agreement Type
  const handleSaveAgreementType = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const typeData: any = {
        name: agreementTypeFormData.name,
        description: agreementTypeFormData.description || null,
        icon_class: agreementTypeFormData.icon_class || null
      };

      if (editingAgreementType) {
        await apiService.updateAgreementType(editingAgreementType.id, typeData);
        setSuccess('Agreement type updated successfully');
      } else {
        await apiService.createAgreementType(typeData);
        setSuccess('Agreement type created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowAgreementTypeModal(false);
      }, 1500);

      // Reload agreement types
      const data = await apiService.getAgreementTypes();
      setAgreementTypes(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save agreement type');
    } finally {
      setSaving(false);
    }
  };

  // Handle Add Status button
  const handleAddStatus = () => {
    setEditingStatus(null);
    setStatusFormData({
      name: '',
      description: '',
      color_class: 'bg-gray-100 text-gray-700',
      sla_days: '',
      sort_order: ''
    });
    setError(null);
    setSuccess(null);
    setShowStatusModal(true);
  };

  // Handle Edit Status button
  const handleEditStatus = (status: any) => {
    setEditingStatus(status);
    setStatusFormData({
      name: status.name || '',
      description: status.description || '',
      color_class: status.color_class || 'bg-gray-100 text-gray-700',
      sla_days: status.sla_days !== null && status.sla_days !== undefined ? String(status.sla_days) : '',
      sort_order: status.sort_order !== null && status.sort_order !== undefined ? String(status.sort_order) : ''
    });
    setError(null);
    setSuccess(null);
    setShowStatusModal(true);
  };

  // Handle Status Input Change
  const handleStatusInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setStatusFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Save Status
  const handleSaveStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const statusData: any = {
        name: statusFormData.name,
        description: statusFormData.description || null,
        color_class: statusFormData.color_class || null,
        sla_days: statusFormData.sla_days !== '' ? Number(statusFormData.sla_days) : null,
        sort_order: statusFormData.sort_order !== '' ? Number(statusFormData.sort_order) : null
      };

      if (editingStatus) {
        await apiService.updateStatus(editingStatus.id, statusData);
        setSuccess('Status updated successfully');
      } else {
        await apiService.createStatus(statusData);
        setSuccess('Status created successfully');
      }

      setTimeout(() => {
        setSuccess(null);
        setShowStatusModal(false);
      }, 1500);

      // Reload statuses
      const data = await apiService.getStatuses();
      setStatuses(data);
    } catch (err: any) {
      setError(err.message || 'Failed to save status');
    } finally {
      setSaving(false);
    }
  };

  const contentTabs = [
    { id: 'company', label: 'Company' },
    { id: 'category', label: 'Category' },
    { id: 'agreement-types', label: 'Agreement Types' },
    { id: 'status', label: 'Status' }
  ];

  return (
    <section className="px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Content Management</h2>
      </div>

      <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border transition-colors duration-300`}>
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex space-x-8 px-8 pt-4">
            {contentTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`pb-4 text-sm font-semibold transition-colors ${
                  activeContentTab === tab.id
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-8">
          {activeContentTab === 'company' && (
            <>
              {error && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                  darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                </div>
              )}
              {success && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg ${
                  darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{success}</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Company Management</h3>
                <button 
                  onClick={handleAddCompany}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Company
                </button>
              </div>
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Loading companies...
                </div>
              ) : companies.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No companies found
                </div>
              ) : (
                <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden transition-colors duration-300`}>
                  <table className="w-full">
                    <thead className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Company Name</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Code</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {companies.map((company) => (
                        <tr key={company.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <CompanyLogo 
                                src={company.logo_url} 
                                name={company.name}
                                id={company.id}
                                className="w-10 h-10 rounded-lg"
                                alt={company.name}
                              />
                              <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{company.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <code className={`text-xs px-2 py-1 rounded ${
                              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}>{company.code}</code>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{company.description || '-'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              company.is_active 
                                ? darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
                                : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {company.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => handleEditCompany(company)}
                                className={`p-2 text-violet-600 rounded transition-colors ${
                                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-violet-50'
                                }`}
                              >
                                <i className="fa-solid fa-edit text-sm"></i>
                              </button>
                              <button 
                                onClick={async () => {
                                  if (window.confirm(`Are you sure you want to delete ${company.name}?`)) {
                                    try {
                                      await apiService.deleteCompany(company.id);
                                      setSuccess('Company deleted successfully');
                                      const data = await apiService.getCompanies();
                                      setCompanies(data);
                                      setTimeout(() => setSuccess(null), 3000);
                                    } catch (err: any) {
                                      setError(err.message || 'Failed to delete company');
                                      setTimeout(() => setError(null), 3000);
                                    }
                                  }
                                }}
                                className={`p-2 text-red-600 rounded transition-colors ${
                                  darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                                }`}
                              >
                                <i className="fa-solid fa-trash text-sm"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Company Modal */}
              {showCompanyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingCompany ? 'Edit Company' : 'Add New Company'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowCompanyModal(false);
                          setError(null);
                          setSuccess(null);
                          setLogoPreview(null);
                          setLogoUploadMode('url');
                        }}
                        className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveCompany} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={companyFormData.name}
                              onChange={handleCompanyInputChange}
                              required
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                                darkMode
                                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="e.g., PT ABC 2025"
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Code <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="code"
                              value={companyFormData.code}
                              onChange={handleCompanyInputChange}
                              required
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                                darkMode
                                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="e.g., ABC2025"
                            />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={companyFormData.description}
                            onChange={handleCompanyInputChange}
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Company description..."
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Company Logo
                          </label>
                          
                          {/* Mode Selection */}
                          <div className="flex items-center space-x-4 mb-3">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="logoMode"
                                value="url"
                                checked={logoUploadMode === 'url'}
                                onChange={() => {
                                  setLogoUploadMode('url');
                                  if (!companyFormData.logo_url.startsWith('data:')) {
                                    setLogoPreview(companyFormData.logo_url || null);
                                  } else {
                                    setCompanyFormData(prev => ({ ...prev, logo_url: '' }));
                                    setLogoPreview(null);
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Use URL</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="logoMode"
                                value="upload"
                                checked={logoUploadMode === 'upload'}
                                onChange={() => {
                                  setLogoUploadMode('upload');
                                  if (companyFormData.logo_url.startsWith('data:')) {
                                    setLogoPreview(companyFormData.logo_url);
                                  } else {
                                    setCompanyFormData(prev => ({ ...prev, logo_url: '' }));
                                    setLogoPreview(null);
                                  }
                                }}
                                className="mr-2"
                              />
                              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Upload Image</span>
                            </label>
                          </div>

                          {/* URL Input */}
                          {logoUploadMode === 'url' && (
                            <input
                              type="url"
                              name="logo_url"
                              value={companyFormData.logo_url.startsWith('data:') ? '' : companyFormData.logo_url}
                              onChange={handleCompanyInputChange}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                                darkMode
                                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="https://example.com/logo.png"
                            />
                          )}

                          {/* File Upload */}
                          {logoUploadMode === 'upload' && (
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                                  darkMode
                                    ? 'bg-gray-900 border-gray-700 text-white'
                                    : 'border-gray-300'
                                }`}
                              />
                              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max size: 2MB. Supported formats: JPG, PNG, GIF</p>
                            </div>
                          )}

                          {/* Logo Preview */}
                          {(logoPreview || companyFormData.logo_url) && (
                            <div className="mt-3">
                              <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Preview:</p>
                              <div className="inline-block">
                                <CompanyLogo 
                                  src={logoPreview || companyFormData.logo_url} 
                                  name={companyFormData.name || 'Company'}
                                  id={editingCompany?.id}
                                  className={`w-16 h-16 rounded-lg border-2 ${
                                    darkMode ? 'border-gray-600' : 'border-gray-200'
                                  }`}
                                  alt="Logo preview"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setCompanyFormData(prev => ({ ...prev, logo_url: '' }));
                                  setLogoPreview(null);
                                }}
                                className={`ml-3 text-xs transition-colors ${
                                  darkMode
                                    ? 'text-red-400 hover:text-red-300'
                                    : 'text-red-600 hover:text-red-700'
                                }`}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="is_active"
                              checked={companyFormData.is_active}
                              onChange={handleCompanyInputChange}
                              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                            />
                            <label className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Active
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t transition-colors duration-300 ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCompanyModal(false);
                            setError(null);
                            setSuccess(null);
                            setLogoPreview(null);
                            setLogoUploadMode('url');
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingCompany ? 'Update Company' : 'Create Company'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

          {activeContentTab === 'category' && (
            <>
              {error && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                </div>
              )}
              {success && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{success}</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Category Management</h3>
                <button 
                  onClick={handleAddCategory}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Category
                </button>
              </div>
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Loading categories...
                </div>
              ) : categories.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No categories found
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6">
                  {categories.map((category, idx) => {
                    const iconClasses = category.icon_class ? category.icon_class.split(' ') : [];
                    const iconName = iconClasses[0] || 'fa-folder';
                    const iconColorClasses = iconClasses.slice(1).join(' ') || 'text-violet-600';
                    
                    // Extract background color from icon classes or use default
                    const bgColorClasses = [
                      'bg-violet-100',
                      'bg-green-100',
                      'bg-purple-100',
                      'bg-blue-100',
                      'bg-pink-100',
                      'bg-orange-100'
                    ];
                    const bgClass = bgColorClasses[idx % bgColorClasses.length];
                    const textColorClass = iconColorClasses.includes('text-') 
                      ? iconColorClasses.split(' ').find((c: string) => c.startsWith('text-')) || 'text-violet-600'
                      : 'text-violet-600';
                    
                    return (
                      <div 
                        key={category.id} 
                        className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-6 hover:shadow-md transition-all duration-300`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className={`w-12 h-12 ${bgClass} rounded-lg flex items-center justify-center`}>
                            <i className={`fa-solid ${iconName} ${textColorClass} text-xl`}></i>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleEditCategory(category)}
                              className={`p-2 rounded transition-colors ${
                                darkMode
                                  ? 'text-gray-400 hover:bg-gray-700'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <i className="fa-solid fa-edit text-sm"></i>
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete ${category.name}?`)) {
                                  try {
                                    await apiService.deleteCategory(category.id);
                                    setSuccess('Category deleted successfully');
                                    const data = await apiService.getCategories();
                                    setCategories(data);
                                    setTimeout(() => setSuccess(null), 3000);
                                  } catch (err: any) {
                                    setError(err.message || 'Failed to delete category');
                                    setTimeout(() => setError(null), 3000);
                                  }
                                }
                              }}
                              className={`p-2 text-red-600 rounded transition-colors ${
                                darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                              }`}
                            >
                              <i className="fa-solid fa-trash text-sm"></i>
                            </button>
                          </div>
                        </div>
                        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{category.name}</h3>
                        {category.description && (
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{category.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Category Modal */}
              {showCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingCategory ? 'Edit Category' : 'Add New Category'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowCategoryModal(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveCategory} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Category Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={categoryFormData.name}
                            onChange={handleCategoryInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="e.g., Legal Documents"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={categoryFormData.description}
                            onChange={handleCategoryInputChange}
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Category description..."
                          />
                        </div>
                        
                        {/* Icon Selection */}
                        <div>
                          <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Icon <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-6 gap-3 mb-4">
                            {[
                              'fa-folder', 'fa-file', 'fa-file-lines', 'fa-folder-open', 'fa-briefcase', 'fa-handshake',
                              'fa-file-contract', 'fa-file-signature', 'fa-clipboard', 'fa-clipboard-list', 'fa-book', 'fa-bookmark',
                              'fa-tag', 'fa-tags', 'fa-certificate', 'fa-circle-check', 'fa-shield-halved', 'fa-shield',
                              'fa-lock', 'fa-key', 'fa-file-alt', 'fa-archive', 'fa-box', 'fa-inbox'
                            ].map((icon) => {
                              const iconParts = categoryFormData.icon_class.split(' ');
                              const currentIcon = iconParts[0] || 'fa-folder';
                              const currentColor = iconParts.find(p => p.startsWith('text-')) || 'text-violet-600';
                              const isSelected = currentIcon === icon;
                              
                              return (
                                <button
                                  key={icon}
                                  type="button"
                                  onClick={() => {
                                    setCategoryFormData(prev => ({
                                      ...prev,
                                      icon_class: `${icon} ${currentColor}`
                                    }));
                                  }}
                                  className={`p-3 border-2 rounded-lg transition-all ${
                                    isSelected 
                                      ? 'border-violet-500 bg-violet-50' 
                                      : darkMode
                                        ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                                >
                                  <i className={`fa-solid ${icon} ${currentColor} text-xl`}></i>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Color Selection */}
                        <div>
                          <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Icon Color <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-8 gap-3">
                            {[
                              { name: 'Violet', class: 'text-violet-600', bg: 'bg-violet-100' },
                              { name: 'Purple', class: 'text-purple-600', bg: 'bg-purple-100' },
                              { name: 'Blue', class: 'text-blue-600', bg: 'bg-blue-100' },
                              { name: 'Green', class: 'text-green-600', bg: 'bg-green-100' },
                              { name: 'Orange', class: 'text-orange-600', bg: 'bg-orange-100' },
                              { name: 'Red', class: 'text-red-600', bg: 'bg-red-100' },
                              { name: 'Pink', class: 'text-pink-600', bg: 'bg-pink-100' },
                              { name: 'Indigo', class: 'text-indigo-600', bg: 'bg-indigo-100' }
                            ].map((color) => {
                              const iconParts = categoryFormData.icon_class.split(' ');
                              const currentIcon = iconParts[0] || 'fa-folder';
                              const currentColor = iconParts.find(p => p.startsWith('text-')) || 'text-violet-600';
                              const isSelected = currentColor === color.class;
                              
                              return (
                                <button
                                  key={color.class}
                                  type="button"
                                  onClick={() => {
                                    setCategoryFormData(prev => ({
                                      ...prev,
                                      icon_class: `${currentIcon} ${color.class}`
                                    }));
                                  }}
                                  className={`p-3 border-2 rounded-lg transition-all flex flex-col items-center ${
                                    isSelected 
                                      ? 'border-violet-500 bg-violet-50' 
                                      : darkMode
                                        ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                                >
                                  <div className={`w-8 h-8 ${color.bg} rounded-lg flex items-center justify-center mb-1`}>
                                    <i className={`fa-solid ${currentIcon} ${color.class} text-sm`}></i>
                                  </div>
                                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{color.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Preview */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Preview
                          </label>
                          <div className={`p-4 border rounded-lg transition-colors duration-300 ${
                            darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center space-x-4">
                              {(() => {
                                const iconParts = categoryFormData.icon_class.split(' ');
                                const iconName = iconParts[0] || 'fa-folder';
                                const colorClass = iconParts.find(p => p.startsWith('text-')) || 'text-violet-600';
                                const bgColorMap: { [key: string]: string } = {
                                  'text-violet-600': 'bg-violet-100',
                                  'text-purple-600': 'bg-purple-100',
                                  'text-blue-600': 'bg-blue-100',
                                  'text-green-600': 'bg-green-100',
                                  'text-orange-600': 'bg-orange-100',
                                  'text-red-600': 'bg-red-100',
                                  'text-pink-600': 'bg-pink-100',
                                  'text-indigo-600': 'bg-indigo-100'
                                };
                                const bgClass = bgColorMap[colorClass] || 'bg-violet-100';
                                
                                return (
                                  <div className={`w-12 h-12 ${bgClass} rounded-lg flex items-center justify-center`}>
                                    <i className={`fa-solid ${iconName} ${colorClass} text-xl`}></i>
                                  </div>
                                );
                              })()}
                              <div>
                                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {categoryFormData.name || 'Category Name'}
                                </h4>
                                {categoryFormData.description && (
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{categoryFormData.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t transition-colors duration-300 ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCategoryModal(false);
                            setError(null);
                            setSuccess(null);
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingCategory ? 'Update Category' : 'Create Category'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

          {activeContentTab === 'agreement-types' && (
            <>
              {error && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                </div>
              )}
              {success && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{success}</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Agreement Types Management</h3>
                <button 
                  onClick={handleAddAgreementType}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Type
                </button>
              </div>
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Loading agreement types...
                </div>
              ) : agreementTypes.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No agreement types found
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6">
                  {agreementTypes.map((type, idx) => {
                    const iconClasses = type.icon_class ? type.icon_class.split(' ') : [];
                    const iconName = iconClasses[0] || 'fa-file-signature';
                    const iconColorClasses = iconClasses.slice(1).join(' ') || 'text-purple-600';
                    
                    // Extract background color from icon classes or use default
                    const bgColorClasses = [
                      'bg-purple-100',
                      'bg-violet-100',
                      'bg-indigo-100',
                      'bg-blue-100',
                      'bg-pink-100',
                      'bg-orange-100'
                    ];
                    const bgClass = bgColorClasses[idx % bgColorClasses.length];
                    const textColorClass = iconColorClasses.includes('text-') 
                      ? iconColorClasses.split(' ').find((c: string) => c.startsWith('text-')) || 'text-purple-600'
                      : 'text-purple-600';
                    
                    return (
                      <div 
                        key={type.id} 
                        className={`border rounded-lg p-6 hover:shadow-md transition-all duration-300 ${
                          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className={`w-12 h-12 ${bgClass} rounded-lg flex items-center justify-center`}>
                            <i className={`fa-solid ${iconName} ${textColorClass} text-xl`}></i>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleEditAgreementType(type)}
                              className={`p-2 rounded transition-colors ${
                                darkMode
                                  ? 'text-gray-400 hover:bg-gray-700'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <i className="fa-solid fa-edit text-sm"></i>
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete ${type.name}?`)) {
                                  try {
                                    await apiService.deleteAgreementType(type.id);
                                    setSuccess('Agreement type deleted successfully');
                                    const data = await apiService.getAgreementTypes();
                                    setAgreementTypes(data);
                                    setTimeout(() => setSuccess(null), 3000);
                                  } catch (err: any) {
                                    setError(err.message || 'Failed to delete agreement type');
                                    setTimeout(() => setError(null), 3000);
                                  }
                                }
                              }}
                              className={`p-2 text-red-600 rounded transition-colors ${
                                darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                              }`}
                            >
                              <i className="fa-solid fa-trash text-sm"></i>
                            </button>
                          </div>
                        </div>
                        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{type.name}</h3>
                        {type.description && (
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{type.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Agreement Type Modal */}
              {showAgreementTypeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingAgreementType ? 'Edit Agreement Type' : 'Add New Agreement Type'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowAgreementTypeModal(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveAgreementType} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Agreement Type Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={agreementTypeFormData.name}
                            onChange={handleAgreementTypeInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="e.g., PKS, MOU, PO"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={agreementTypeFormData.description}
                            onChange={handleAgreementTypeInputChange}
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Agreement type description..."
                          />
                        </div>
                        
                        {/* Icon Selection */}
                        <div>
                          <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Icon <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-6 gap-3 mb-4">
                            {[
                              'fa-folder', 'fa-file', 'fa-file-lines', 'fa-folder-open', 'fa-briefcase', 'fa-handshake',
                              'fa-file-contract', 'fa-file-signature', 'fa-clipboard', 'fa-clipboard-list', 'fa-book', 'fa-bookmark',
                              'fa-tag', 'fa-tags', 'fa-certificate', 'fa-circle-check', 'fa-shield-halved', 'fa-shield',
                              'fa-lock', 'fa-key', 'fa-file-alt', 'fa-archive', 'fa-box', 'fa-inbox'
                            ].map((icon) => {
                              const iconParts = agreementTypeFormData.icon_class.split(' ');
                              const currentIcon = iconParts[0] || 'fa-file-signature';
                              const currentColor = iconParts.find(p => p.startsWith('text-')) || 'text-purple-600';
                              const isSelected = currentIcon === icon;
                              
                              return (
                                <button
                                  key={icon}
                                  type="button"
                                  onClick={() => {
                                    setAgreementTypeFormData(prev => ({
                                      ...prev,
                                      icon_class: `${icon} ${currentColor}`
                                    }));
                                  }}
                                  className={`p-3 border-2 rounded-lg transition-all ${
                                    isSelected 
                                      ? 'border-violet-500 bg-violet-50' 
                                      : darkMode
                                        ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                                >
                                  <i className={`fa-solid ${icon} ${currentColor} text-xl`}></i>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Color Selection */}
                        <div>
                          <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Icon Color <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-8 gap-3">
                            {[
                              { name: 'Violet', class: 'text-violet-600', bg: 'bg-violet-100' },
                              { name: 'Purple', class: 'text-purple-600', bg: 'bg-purple-100' },
                              { name: 'Blue', class: 'text-blue-600', bg: 'bg-blue-100' },
                              { name: 'Green', class: 'text-green-600', bg: 'bg-green-100' },
                              { name: 'Orange', class: 'text-orange-600', bg: 'bg-orange-100' },
                              { name: 'Red', class: 'text-red-600', bg: 'bg-red-100' },
                              { name: 'Pink', class: 'text-pink-600', bg: 'bg-pink-100' },
                              { name: 'Indigo', class: 'text-indigo-600', bg: 'bg-indigo-100' }
                            ].map((color) => {
                              const iconParts = agreementTypeFormData.icon_class.split(' ');
                              const currentIcon = iconParts[0] || 'fa-file-signature';
                              const currentColor = iconParts.find(p => p.startsWith('text-')) || 'text-purple-600';
                              const isSelected = currentColor === color.class;
                              
                              return (
                                <button
                                  key={color.class}
                                  type="button"
                                  onClick={() => {
                                    setAgreementTypeFormData(prev => ({
                                      ...prev,
                                      icon_class: `${currentIcon} ${color.class}`
                                    }));
                                  }}
                                  className={`p-3 border-2 rounded-lg transition-all flex flex-col items-center ${
                                    isSelected 
                                      ? 'border-violet-500 bg-violet-50' 
                                      : darkMode
                                        ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                                >
                                  <div className={`w-8 h-8 ${color.bg} rounded-lg flex items-center justify-center mb-1`}>
                                    <i className={`fa-solid ${currentIcon} ${color.class} text-sm`}></i>
                                  </div>
                                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{color.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Preview */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Preview
                          </label>
                          <div className={`p-4 border rounded-lg transition-colors duration-300 ${
                            darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center space-x-4">
                              {(() => {
                                const iconParts = agreementTypeFormData.icon_class.split(' ');
                                const iconName = iconParts[0] || 'fa-file-signature';
                                const colorClass = iconParts.find(p => p.startsWith('text-')) || 'text-purple-600';
                                const bgColorMap: { [key: string]: string } = {
                                  'text-violet-600': 'bg-violet-100',
                                  'text-purple-600': 'bg-purple-100',
                                  'text-blue-600': 'bg-blue-100',
                                  'text-green-600': 'bg-green-100',
                                  'text-orange-600': 'bg-orange-100',
                                  'text-red-600': 'bg-red-100',
                                  'text-pink-600': 'bg-pink-100',
                                  'text-indigo-600': 'bg-indigo-100'
                                };
                                const bgClass = bgColorMap[colorClass] || 'bg-purple-100';
                                
                                return (
                                  <div className={`w-12 h-12 ${bgClass} rounded-lg flex items-center justify-center`}>
                                    <i className={`fa-solid ${iconName} ${colorClass} text-xl`}></i>
                                  </div>
                                );
                              })()}
                              <div>
                                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {agreementTypeFormData.name || 'Agreement Type Name'}
                                </h4>
                                {agreementTypeFormData.description && (
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{agreementTypeFormData.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t transition-colors duration-300 ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAgreementTypeModal(false);
                            setError(null);
                            setSuccess(null);
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingAgreementType ? 'Update Agreement Type' : 'Create Agreement Type'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

          {activeContentTab === 'status' && (
            <>
              {error && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                </div>
              )}
              {success && (
                <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                  darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                }`}>
                  <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{success}</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Status Management</h3>
                <button 
                  onClick={handleAddStatus}
                  className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Status
                </button>
              </div>
              {loading ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Loading statuses...
                </div>
              ) : statuses.length === 0 ? (
                <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No statuses found
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-6">
                  {statuses.map((status, idx) => {
                    const badgeClasses = status.color_class || 'bg-gray-100 text-gray-700';
                    
                    // Extract background color from badge classes or use default
                    const bgColorClasses = [
                      'bg-gray-100',
                      'bg-green-100',
                      'bg-blue-100',
                      'bg-yellow-100',
                      'bg-red-100',
                      'bg-purple-100'
                    ];
                    const bgClass = bgColorClasses[idx % bgColorClasses.length];
                    const textColorClass = badgeClasses.includes('text-') 
                      ? badgeClasses.split(' ').find((c: string) => c.startsWith('text-')) || 'text-gray-700'
                      : 'text-gray-700';
                    
                    return (
                      <div 
                        key={status.id} 
                        className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-6 hover:shadow-md transition-all duration-300`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className={`w-12 h-12 ${bgClass} rounded-lg flex items-center justify-center`}>
                            <i className={`fa-solid fa-tag ${textColorClass} text-xl`}></i>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button 
                              onClick={() => handleEditStatus(status)}
                              className={`p-2 rounded transition-colors ${
                                darkMode
                                  ? 'text-gray-400 hover:bg-gray-700'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <i className="fa-solid fa-edit text-sm"></i>
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete ${status.name}?`)) {
                                  try {
                                    await apiService.deleteStatus(status.id);
                                    setSuccess('Status deleted successfully');
                                    const data = await apiService.getStatuses();
                                    setStatuses(data);
                                    setTimeout(() => setSuccess(null), 3000);
                                  } catch (err: any) {
                                    setError(err.message || 'Failed to delete status');
                                    setTimeout(() => setError(null), 3000);
                                  }
                                }
                              }}
                              className={`p-2 text-red-600 rounded transition-colors ${
                                darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                              }`}
                            >
                              <i className="fa-solid fa-trash text-sm"></i>
                            </button>
                          </div>
                        </div>
                        <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          <span className={`px-2 py-1 text-xs font-medium ${badgeClasses} rounded`}>
                            {status.name}
                          </span>
                        </h3>
                        {status.description && (
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{status.description}</p>
                        )}
                        <div className={`mt-3 flex items-center gap-4 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span><i className="fa-regular fa-clock mr-1"></i>SLA: {status.sla_days !== null && status.sla_days !== undefined ? `${status.sla_days} hari` : '-'}</span>
                          <span><i className="fa-solid fa-list-ol mr-1"></i>Urutan: {status.sort_order ?? '-'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Status Modal */}
              {showStatusModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between transition-colors duration-300 ${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {editingStatus ? 'Edit Status' : 'Add New Status'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowStatusModal(false);
                          setError(null);
                          setSuccess(null);
                        }}
                        className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}
                      >
                        <i className="fa-solid fa-times text-xl"></i>
                      </button>
                    </div>

                    <form onSubmit={handleSaveStatus} className="p-6">
                      {error && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                            <i className="fa-solid fa-exclamation-circle mr-2"></i>
                            {error}
                          </p>
                        </div>
                      )}
                      {success && (
                        <div className={`mb-4 p-3 border-l-4 rounded-lg transition-colors duration-300 ${
                          darkMode ? 'bg-green-900/20 border-green-500' : 'bg-green-50 border-green-500'
                        }`}>
                          <p className={`text-sm flex items-center ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                            <i className="fa-solid fa-check-circle mr-2"></i>
                            {success}
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Status Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={statusFormData.name}
                            onChange={handleStatusInputChange}
                            required
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="e.g., NEW, DRAFTING, APPROVED"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={statusFormData.description}
                            onChange={handleStatusInputChange}
                            rows={3}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                              darkMode
                                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                : 'border-gray-300'
                            }`}
                            placeholder="Status description..."
                          />
                        </div>

                        {/* SLA & workflow order (used by Summary page) */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              SLA (hari)
                            </label>
                            <input
                              type="number"
                              min={0}
                              name="sla_days"
                              value={statusFormData.sla_days}
                              onChange={handleStatusInputChange}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                                darkMode
                                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="Kosongkan = tanpa SLA"
                            />
                            <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Batas maksimal task berada di status ini.</p>
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Urutan Workflow
                            </label>
                            <input
                              type="number"
                              name="sort_order"
                              value={statusFormData.sort_order}
                              onChange={handleStatusInputChange}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm ${
                                darkMode
                                  ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                  : 'border-gray-300'
                              }`}
                              placeholder="1, 2, 3 ..."
                            />
                            <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Urutan tahapan di halaman Summary.</p>
                          </div>
                        </div>
                        
                        {/* Color Selection for Status Badge */}
                        <div>
                          <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Badge Color <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-4 gap-3">
                            {[
                              { name: 'Gray', bg: 'bg-gray-100', text: 'text-gray-700' },
                              { name: 'Green', bg: 'bg-green-100', text: 'text-green-700' },
                              { name: 'Blue', bg: 'bg-blue-100', text: 'text-blue-700' },
                              { name: 'Yellow', bg: 'bg-yellow-100', text: 'text-yellow-700' },
                              { name: 'Orange', bg: 'bg-orange-100', text: 'text-orange-700' },
                              { name: 'Red', bg: 'bg-red-100', text: 'text-red-700' },
                              { name: 'Purple', bg: 'bg-purple-100', text: 'text-purple-700' },
                              { name: 'Violet', bg: 'bg-violet-100', text: 'text-violet-700' }
                            ].map((color) => {
                              const colorClass = `${color.bg} ${color.text}`;
                              const isSelected = statusFormData.color_class === colorClass;
                              
                              return (
                                <button
                                  key={colorClass}
                                  type="button"
                                  onClick={() => {
                                    setStatusFormData(prev => ({
                                      ...prev,
                                      color_class: colorClass
                                    }));
                                  }}
                                  className={`p-4 border-2 rounded-lg transition-all ${
                                    isSelected 
                                      ? 'border-violet-500 bg-violet-50' 
                                      : darkMode
                                        ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                  }`}
                                >
                                  <div className="flex flex-col items-center">
                                    <span className={`px-3 py-1 text-xs font-medium ${colorClass} rounded mb-2`}>
                                      {statusFormData.name || 'Status'}
                                    </span>
                                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{color.name}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Preview */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Preview
                          </label>
                          <div className={`p-4 border rounded-lg transition-colors duration-300 ${
                            darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center space-x-4">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                darkMode ? 'bg-gray-600' : 'bg-gray-100'
                              }`}>
                                <i className={`fa-solid fa-tag ${statusFormData.color_class.split(' ').find(c => c.startsWith('text-')) || 'text-gray-700'} text-xl`}></i>
                              </div>
                              <div>
                                <h4 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  <span className={`px-2 py-1 text-xs font-medium ${statusFormData.color_class} rounded`}>
                                    {statusFormData.name || 'Status Name'}
                                  </span>
                                </h4>
                                {statusFormData.description && (
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{statusFormData.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`flex items-center justify-end space-x-3 mt-6 pt-4 border-t transition-colors duration-300 ${
                        darkMode ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        <button
                          type="button"
                          onClick={() => {
                            setShowStatusModal(false);
                            setError(null);
                            setSuccess(null);
                          }}
                          className={`px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                            darkMode
                              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={saving}
                          className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {saving ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-save mr-2"></i>
                              {editingStatus ? 'Update Status' : 'Create Status'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

interface AdminProps {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  activeSubTab?: string;
  onSubTabChange?: (subTab: string) => void;
}

const Admin: React.FC<AdminProps> = ({ activeTab: propActiveTab, onTabChange, activeSubTab, onSubTabChange }) => {
  const [activeTab, setActiveTab] = useState(propActiveTab || 'access');
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState({
    action: '',
    resource_type: '',
    user_id: ''
  });
  const [logsPage, setLogsPage] = useState(0);
  const [logsLimit] = useState(50);
  const [usersForLogs, setUsersForLogs] = useState<any[]>([]);
  const [userScopes, setUserScopes] = useState<string[]>([]);
  const [scopesLoading, setScopesLoading] = useState(true);

  useEffect(() => {
    if (propActiveTab) {
      setActiveTab(propActiveTab);
    }
  }, [propActiveTab]);

  // Load user scopes on mount - use scopes from user object if available, otherwise fetch from API
  useEffect(() => {
    const loadUserScopes = async () => {
      try {
        setScopesLoading(true);
        // First try to use scopes from user object (from /me endpoint)
        if (user?.scopes && user.scopes.length > 0) {
          setUserScopes(user.scopes);
          setScopesLoading(false);
          return;
        }
        
        // Fallback: fetch scopes from API if not in user object
        const scopes = await apiService.getCurrentUserScopes();
        setUserScopes(scopes);
      } catch (error) {
        console.error('Error loading user scopes:', error);
        setUserScopes([]);
      } finally {
        setScopesLoading(false);
      }
    };
    loadUserScopes();
  }, [user]);

  // Load users for logs filter
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await apiService.getUsers();
        setUsersForLogs(usersData);
      } catch (error) {
        console.error('Error loading users for logs:', error);
      }
    };
    if (activeTab === 'logs') {
      loadUsers();
    }
  }, [activeTab]);

  // Load logs when logs tab is active or filter/pagination changes
  useEffect(() => {
    const loadLogs = async () => {
      if (activeTab !== 'logs') return;
      
      setLogsLoading(true);
      try {
        const params: any = {
          skip: logsPage * logsLimit,
          limit: logsLimit
        };
        if (logsFilter.action) params.action = logsFilter.action;
        if (logsFilter.resource_type) params.resource_type = logsFilter.resource_type;
        if (logsFilter.user_id) params.user_id = parseInt(logsFilter.user_id);
        
        const logsData = await apiService.getLogs(params);
        setLogs(logsData);
      } catch (error) {
        console.error('Error loading logs:', error);
      } finally {
        setLogsLoading(false);
      }
    };
    loadLogs();
  }, [activeTab, logsPage, logsFilter.action, logsFilter.resource_type, logsFilter.user_id, logsLimit]);

  // Define tabs with their required scopes
  const allTabs = [
    { id: 'access', label: 'Access Management', icon: 'fa-shield-halved', scope: 'admin.access' },
    { id: 'content', label: 'Content Management', icon: 'fa-folder-open', scope: 'admin.content' },
    { id: 'logs', label: 'Log / History', icon: 'fa-clock-rotate-left', scope: 'admin.logs' }
  ];

  // Filter tabs based on user scopes
  const tabs = allTabs.filter(tab => {
    if (scopesLoading) return false; // Hide all tabs while loading
    return userScopes.includes(tab.scope);
  });

  // Auto-redirect to first available tab if current tab is not accessible
  useEffect(() => {
    if (!scopesLoading && tabs.length > 0) {
      const currentTabExists = tabs.some(tab => tab.id === activeTab);
      if (!currentTabExists) {
        setActiveTab(tabs[0].id);
        if (onTabChange) {
          onTabChange(tabs[0].id);
        }
      }
    }
  }, [scopesLoading, tabs, activeTab, onTabChange]);

  // Handler for filter changes
  const handleFilterChange = (field: string, value: string) => {
    setLogsFilter({ ...logsFilter, [field]: value });
    setLogsPage(0); // Reset pagination when filter changes
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString('id-ID', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLogType = (log: any): string => {
    if (log.action.includes('create') || log.action.includes('update') || log.action.includes('delete')) {
      return 'user';
    }
    if (log.action.includes('login') || log.action.includes('logout')) {
      return 'user';
    }
    return 'system';
  };

  const getUserName = (userId: number | null): string => {
    if (!userId) return 'System';
    const user = usersForLogs.find(u => u.id === userId);
    return user ? user.name : `User #${userId}`;
  };

  // Export logs to Excel
  const handleExportLogs = async () => {
    try {
      setLogsLoading(true);
      
      // Fetch all logs with current filters using pagination
      const allLogs: any[] = [];
      const batchSize = 1000; // Max limit allowed by backend
      let skip = 0;
      let hasMore = true;
      
      const baseParams: any = {};
      if (logsFilter.action) baseParams.action = logsFilter.action;
      if (logsFilter.resource_type) baseParams.resource_type = logsFilter.resource_type;
      if (logsFilter.user_id) baseParams.user_id = parseInt(logsFilter.user_id);
      
      // Fetch all logs in batches
      while (hasMore) {
        const params = {
          ...baseParams,
          skip: skip,
          limit: batchSize
        };
        
        const batchLogs = await apiService.getLogs(params);
        allLogs.push(...batchLogs);
        
        // If we got less than batchSize, we've reached the end
        if (batchLogs.length < batchSize) {
          hasMore = false;
        } else {
          skip += batchSize;
        }
      }
      
      // Check if there are logs to export
      if (!allLogs || allLogs.length === 0) {
        alert('Tidak ada data logs untuk diekspor.');
        setLogsLoading(false);
        return;
      }
      
      // Format data for Excel
      const excelData = allLogs.map((log: any, index: number) => ({
        'No': index + 1,
        'User': getUserName(log.user_id),
        'Action': (log.action || '').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        'Resource Type': log.resource_type || '-',
        'Resource ID': log.resource_id || '-',
        'Description': log.description || '-',
        'IP Address': log.ip_address || '-',
        'User Agent': log.user_agent || '-',
        'Created At': log.created_at ? new Date(log.created_at).toLocaleString('id-ID', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }) : '-'
      }));
      
      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      const colWidths = [
        { wch: 5 },   // No
        { wch: 20 },  // User
        { wch: 25 },  // Action
        { wch: 15 },  // Resource Type
        { wch: 12 },  // Resource ID
        { wch: 40 },  // Description
        { wch: 15 },  // IP Address
        { wch: 50 },  // User Agent
        { wch: 20 }   // Created At
      ];
      ws['!cols'] = colWidths;
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Activity Logs');
      
      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const filename = `logs_${dateStr}.xlsx`;
      
      // Download file
      XLSX.writeFile(wb, filename);
      
      setLogsLoading(false);
    } catch (error: any) {
      console.error('Error exporting logs:', error);
      setLogsLoading(false);
      const errorMessage = error?.message || 'Gagal mengekspor logs. Silakan coba lagi.';
      alert(errorMessage);
    }
  };

  // If no tabs available after filtering, show message
  if (!scopesLoading && tabs.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className={`text-2xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Akses Ditolak</h2>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Anda tidak memiliki permission untuk mengakses Admin Panel.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <section 
        className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border-b px-6 py-4 fixed z-30 transition-all duration-300`}
        style={{ 
          left: isCollapsed ? '64px' : '256px',
          right: 0,
          top: '56px'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Admin Panel</h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Manage users, groups, permissions, and system settings</p>
          </div>
        </div>
      </section>

      <section 
        className={`${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} border-b px-6 py-4 fixed z-20 transition-all duration-300`}
        style={{ 
          left: isCollapsed ? '64px' : '256px',
          right: 0,
          top: '140px'
        }}
      >
        <div className={`border-b ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`pb-3 text-sm font-semibold flex items-center space-x-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : darkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={`fa-solid ${tab.icon}`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </section>

      <div style={{ marginTop: '142px' }}>

        {/* Access Management Tab */}
        {activeTab === 'access' && (
          <AccessManagement activeSubTab={activeSubTab} onSubTabChange={onSubTabChange} />
        )}

        {/* Content Management Tab */}
        {activeTab === 'content' && (
          <ContentManagement activeSubTab={activeSubTab} onSubTabChange={onSubTabChange} />
        )}

        {/* Log / History Tab */}
        {activeTab === 'logs' && (
          <section className="px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Activity Log / History</h2>
              <div className="flex items-center space-x-3">
                <select 
                  value={logsFilter.action}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500"
                >
                  <option value="">All Actions</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                  <option value="create_user">Create User</option>
                  <option value="update_user">Update User</option>
                  <option value="delete_user">Delete User</option>
                  <option value="create_group">Create Group</option>
                  <option value="update_group">Update Group</option>
                  <option value="delete_group">Delete Group</option>
                  <option value="create_page">Create Page</option>
                  <option value="update_page">Update Page</option>
                  <option value="delete_page">Delete Page</option>
                </select>
                <select 
                  value={logsFilter.resource_type}
                  onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500"
                >
                  <option value="">All Resources</option>
                  <option value="user">User</option>
                  <option value="group">Group</option>
                  <option value="page">Page</option>
                </select>
                <select 
                  value={logsFilter.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500"
                >
                  <option value="">All Users</option>
                  {usersForLogs.map(user => (
                    <option key={user.id} value={user.id.toString()}>{user.name}</option>
                  ))}
                </select>
                <button 
                  onClick={handleExportLogs}
                  disabled={logsLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {logsLoading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-download mr-2"></i>
                      Export Logs
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {logsLoading ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Loading logs...
                </div>
              ) : logs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No logs found
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {logs.map((log) => {
                    const logType = getLogType(log);
                    return (
                      <div key={log.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              logType === 'user' ? 'bg-violet-100' : 'bg-gray-100'
                            }`}>
                              <i className={`fa-solid ${
                                logType === 'user' ? 'fa-user' : 'fa-gear'
                              } text-sm ${
                                logType === 'user' ? 'text-violet-600' : 'text-gray-600'
                              }`}></i>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {getUserName(log.user_id)}
                                </span>
                                <span className="text-sm text-gray-600 capitalize">
                                  {log.action.replace(/_/g, ' ')}
                                </span>
                                {log.resource_type && (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                    {log.resource_type}
                                  </span>
                                )}
                              </div>
                              {log.description && (
                                <p className="text-sm text-gray-600 mt-1">{log.description}</p>
                              )}
                              <div className="flex items-center space-x-4 mt-1">
                                <p className="text-xs text-gray-500">{formatTimeAgo(log.created_at)}</p>
                                {log.ip_address && (
                                  <p className="text-xs text-gray-400">IP: {log.ip_address}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Pagination */}
              {logs.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {logsPage * logsLimit + 1} - {Math.min((logsPage + 1) * logsLimit, logs.length)} of {logs.length} logs
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setLogsPage(Math.max(0, logsPage - 1))}
                      disabled={logsPage === 0}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setLogsPage(logsPage + 1)}
                      disabled={logs.length < logsLimit}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

      </div>
    </>
  );
};

export default Admin;

